import type { WatchQueryFetchPolicy } from '@apollo/client';
import { useMemo } from 'react';

import type { ExtendedAdsQueryVariables } from 'src/components/ad/SearchAdsPage';
import { useAdsQuery } from 'src/hooks/__generated__/queries';
import type { Geometry } from 'src/types/__generated__/graphql';
import { AdStatuses } from 'src/types/__generated__/graphql';
import { getIntersectionFromQueryVariablesAndMapBounds } from '../utils/getIntersectionFromQueryVariablesAndMapBounds';
import type { ViewMode } from '../types/viewMode';

type VariablesWithMapBounds = ExtendedAdsQueryVariables & { noIntersection?: boolean };

type Args = {
    queryVariables: ExtendedAdsQueryVariables;
    fetchPolicy: WatchQueryFetchPolicy;
    mapBounds?: Geometry;
    viewMode: ViewMode;
};

export const useAds = ({ queryVariables, fetchPolicy, mapBounds, viewMode }: Args) => {
    // No need to fetch ads if the user don't see them on the map, so we get the intersection of the map bounds (what the user sees) and the queryVariables.places
    // and fetch the ads located in this intersection
    const queryVariablesWithMapBounds = useMemo<VariablesWithMapBounds>(() => {
        if (viewMode === 'list') {
            return queryVariables;
        }
        return getIntersectionFromQueryVariablesAndMapBounds({ queryVariables, mapBounds });
    }, [mapBounds, queryVariables, viewMode]);

    const {
        previousData,
        data = previousData,
        fetchMore,
        loading,
    } = useAdsQuery({
        variables: {
            ...queryVariablesWithMapBounds,
            statuses: [AdStatuses.Available],
        },
        fetchPolicy,
        notifyOnNetworkStatusChange: true,
        skip: Boolean(
            queryVariablesWithMapBounds.noIntersection ||
                (viewMode === 'map' &&
                    !queryVariablesWithMapBounds.places?.length &&
                    !queryVariablesWithMapBounds.place),
        ),
    });

    return {
        ads: queryVariablesWithMapBounds.noIntersection ? [] : data?.ads?.items,
        totalAds: queryVariablesWithMapBounds.noIntersection ? 0 : data?.ads?.totalCount,
        loading,
        fetchMore,
    };
};
